import { createContext, useContext, useEffect, useState } from "react";

export const EntryContext = createContext<
  | {
      state: StateWrapper<EntryStatus>;
      update: (action: ExecuteOnContext<EntryStatus>) => void;
    }
  | undefined
>(undefined);

export const initiateEntryContext = (state: EntryStatus) => {
  var stateWrapper = {
    state: state,
    subStateSelector: [],
  };
  return {
    state: stateWrapper,
    update: (action: ExecuteOnContext<EntryStatus>) =>
      entryContextUpdator(stateWrapper, action),
  };
};

//#region
export interface StateWrapper<T> {
  state: T;
  subStateSelector: SubState<T, any>[];
}

export interface SubState<T, T2> {
  selector: (state: T) => T2;
  setSubState: ((subState: T2) => void)[];
  lastState: string;
}

export interface EntryStatus {
  confinedSpaceStatus: ConfinedSpaceStatus;
  realTimeStatus: RealTimeStatus;
  weatherStatus: WeatherStatus;
  siteEquipments: any[];
  isAllowedCheckIn: boolean;
}

export interface ConfinedSpaceStatus {
  pm2_5?: number;
  temperature?: number;
}

export interface RealTimeStatus {
  total: number;
  standBy: number;
  in: number;
  out: number;
  inAI: number;
  outAI: number;
  workerStatuses: WorkerStatus[];
}

export interface WorkerStatus {
  id: string;
  equipmentIds: string[];
  name: string;
  heartRate?: string;
  isOnline: boolean;
  realTimeResourceTypeId?: number[];
  equipments?: any[];
  type?: string;
  oxygen?: string;
  bloodOxygen?: string;
  bodyTemperature?: string;
  watchLatitude?: number;
  watchLongitude?: number;
  showGasGPS?: boolean;
  showWatchGPS?: boolean;
  co2?: number;
  lel?: number;
  o2?: number;
  h2s?: number;
  ch4?: number;
  co?: number;
  lastBloodPressureHigher?: number;
  lastBloodPressureLower?: number;
  position?: {
    place_id?: number;
    lat?: number;
    lng?: number;
    name: string;
    detail: string;
  };
  noOfEntries: {
    number: number;
    timeIn?: Date;
    timeOut?: Date;
  }[];
}

export interface WeatherStatus {
  location: string;
}

export type ExecuteOnContext<T> = (stateWrapper: StateWrapper<T>) => void;
export const Actions = {
  initiate: ((selector, setSubState) => (stateWrapper) => {
    var subStateSelector = stateWrapper.subStateSelector.find(
      (x) => x.selector.toString() == selector.toString()
    );
    if (subStateSelector != undefined)
      subStateSelector.setSubState.push(setSubState);
    else {
      stateWrapper.subStateSelector.push({
        selector: selector,
        setSubState: [setSubState],
        lastState: JSON.stringify(selector(stateWrapper.state)),
      });
    }
  }) as <T1, T2>(
    selector: (state: T1) => T2,
    setSubState: (subState: T2) => void
  ) => ExecuteOnContext<T1>,
  pm2_5: ((pm2_5) => (stateWrapper) => {
    stateWrapper.state.confinedSpaceStatus!.pm2_5 = pm2_5;
  }) as (pm2_5: number) => ExecuteOnContext<EntryStatus>,
  confirmOut: ((workerId) => (stateWrapper) => {
    var entry = stateWrapper
      .state!.realTimeStatus.workerStatuses.find((x) => x.id === workerId)!
      .noOfEntries.at(-1);
    if (entry) entry.timeOut = new Date();
  }) as (workerId: string) => ExecuteOnContext<EntryStatus>,
  setWorkers: ((workers, schedule) => (stateWrapper) => {
    console.log("Setting up workers:", workers);
    stateWrapper.state!.realTimeStatus.workerStatuses = workers.map((x) => {
      console.log("Processing worker:", x.id, "with equipment:", x.equipments);
      console.log(
        "Worker equipment details:",
        x.equipments?.map(
          (e: {
            id: any;
            serialNumber: any;
            realTimeResourceTypeId: any;
            realTimeResourceHub: any;
          }) => ({
            id: e.id,
            serialNumber: e.serialNumber,
            realTimeResourceTypeId: e.realTimeResourceTypeId,
            realTimeResourceHub: e.realTimeResourceHub,
          })
        )
      );
      return {
        id: x.id,
        name: x.name,
        isOnline: false,
        type: x.typeId,
        realTimeResourceTypeId:
          x.equipments?.map((x2: any) => x2.realTimeResourceTypeId) || [],
        equipmentIds: x.equipments?.map((x2: any) => x2.serialNumber) || [],
        equipments: x.equipments || [],
        bloodOxygen: x.bloodOxygen,
        heartRate: x.heartRate,
        watchLatitude: x.watchLatitude,
        watchLongitude: x.watchLongitude,
        lastBloodPressureHigher: x.lastBloodPressureHigher,
        lastBloodPressureLower: x.lastBloodPressureLower,
        showGasGPS: true,
        showWatchGPS: true,
        position: {
          lat: schedule?.lat,
          lng: schedule?.lng,
          name: x.name,
          detail: "",
        },
        noOfEntries: [
          {
            number: 1,
            timeIn: x.timeIn,
            timeOut: x.timeOut,
          },
        ],
      };
    });
  }) as (workers: any[], schedule: any) => ExecuteOnContext<EntryStatus>,
  updateGasSensor: ((gasSensorDatas) => (stateWrapper) => {
    gasSensorDatas.forEach((gasSensorData: any) => {
      stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
        (worker: WorkerStatus) => {
          if (
            worker.equipmentIds.includes(gasSensorData.id) &&
            worker.realTimeResourceTypeId?.includes(1)
          ) {
            worker.ch4 = gasSensorData.resource.find(
              (x: any) => x.gas_type === "LEL-MPS"
            )?.reading;
            worker.co2 = gasSensorData.resource.find(
              (x: any) => x.gas_type === "CO2"
            )?.reading;
            worker.o2 = gasSensorData.resource.find(
              (x: any) => x.gas_type === "O2"
            )?.reading;
            worker.h2s = gasSensorData.resource.find(
              (x: any) => x.gas_type === "H2S"
            )?.reading;
            worker.co = gasSensorData.resource.find(
              (x: any) => x.gas_type === "CO"
            )?.reading;
            worker.isOnline = gasSensorData?.is_online;
            worker.position!.lat = gasSensorData?.location?.latitude;
            worker.position!.lng = gasSensorData?.location?.longitude;
          }
        }
      );
    });
    // console.log("workerStatuses", workerStatuses)
    stateWrapper.state.realTimeStatus.total =
      stateWrapper.state!.realTimeStatus.workerStatuses.length;
    // Dave 20240528
    // Change to set in/out count from the camera instead
    // stateWrapper.state.realTimeStatus.in = stateWrapper.state!.realTimeStatus.workerStatuses.filter(x => x.noOfEntries[x.noOfEntries.length - 1].timeIn).length - stateWrapper.state!.realTimeStatus.workerStatuses.filter(x => x.noOfEntries[x.noOfEntries.length - 1].timeOut).length
    // stateWrapper.state.realTimeStatus.out = stateWrapper.state!.realTimeStatus.workerStatuses.filter(x => x.noOfEntries[x.noOfEntries.length - 1].timeOut).length
    // stateWrapper.state.realTimeStatus.standBy = 2
    // stateWrapper.state.realTimeStatus.standBy = stateWrapper.state!.realTimeStatus.workerStatuses?.filter(x => x.type == '2').length
    // console.log("done: ", stateWrapper.subStateSelector.length)
  }) as (gasSensorDatas: any) => ExecuteOnContext<EntryStatus>,
updateHeartRateSensor: ((heartRateSensorDatas) => (stateWrapper) => {

    // Handle both array and single value cases
    const heartRateData = Array.isArray(heartRateSensorDatas)
        ? heartRateSensorDatas[0]
        : heartRateSensorDatas;

    // Ensure data has sn and value
    const formattedData =
        typeof heartRateData === "number"
            ? { value: heartRateData, sn: "unknown" }
            : heartRateData;

    stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
        (worker: WorkerStatus) => {
            if (worker.equipments && worker.equipments.length > 0) {
                console.log("Processing worker:", worker.name, "ID:", worker.id);
                const heartRateEquipment = worker.equipments.find(
                    (e: any) => e.realTimeResourceTypeId === 2 || e.realTimeResourceTypeId === 6
                );
                if (heartRateEquipment && heartRateEquipment.serialNumber === formattedData.sn) {
                    console.log("Matching equipment found for worker:", worker.name);
                    worker.heartRate = formattedData.value.toString();
                    console.log("Updated heart rate for", worker.name, "to", worker.heartRate);
                }
            }
        }
    );
}) as (heartRateSensorDatas: any) => ExecuteOnContext<EntryStatus>,
  updateBloodOxygenSensor: ((bloodOxygenSensorDatas) => (stateWrapper) => {
    bloodOxygenSensorDatas.forEach((bloodOxygenSensorData: any) => {
      stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
        (worker: WorkerStatus) => {
          // if (worker.equipmentIds.includes(bloodOxygenSensorData.id) && worker.realTimeResourceTypeId === 2) {
          if (
            worker.equipmentIds.includes(bloodOxygenSensorData.sn) &&
            worker.realTimeResourceTypeId?.includes(2)
          ) {
            // test
            worker.bloodOxygen = bloodOxygenSensorData.value;
            // worker.heartRate = bloodOxygenSensorData.resource.find((x: any) => x.value)?.reading;
          }
        }
      );
    });
  }) as (bloodOxygenSensorDatas: any) => ExecuteOnContext<EntryStatus>,
  updateGPSSensor: ((GPSSensorDatas) => (stateWrapper) => {
    GPSSensorDatas.forEach((GPSSensorData: any) => {
      // console.log("GPSSensorData id 1", GPSSensorData)
      stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
        (worker: WorkerStatus) => {
          // console.log("GPSSensorData id 2 ", GPSSensorData)
          // console.log("worker.equipmentIds", worker.equipmentIds)
          // if (worker.equipmentIds.includes(bloodOxygenSensorData.id) && worker.realTimeResourceTypeId === 2) {
          if (
            worker.equipmentIds.includes(GPSSensorData.sn) &&
            worker.realTimeResourceTypeId?.includes(2)
          ) {
            // test
            worker.watchLatitude = GPSSensorData.latitude;
            worker.watchLongitude = GPSSensorData.longitude;
            // worker.heartRate = bloodOxygenSensorData.resource.find((x: any) => x.value)?.reading;
          }
        }
      );
    });
  }) as (GPSSensorDatas: any) => ExecuteOnContext<EntryStatus>,
  updateZetronSensor: ((ZetronSensorDatas) => (stateWrapper) => {
    ZetronSensorDatas.forEach((ZetronSensorData: any) => {
      // console.log("ZetronSensorData id 1", ZetronSensorData)
      ZetronSensorData.Data.forEach((gasRecord: any) => {
        if (gasRecord.ChannelCode == 1)
          stateWrapper.state!.confinedSpaceStatus.pm2_5 = gasRecord.GasVol;
        else if (gasRecord.ChannelCode == 2)
          stateWrapper.state!.confinedSpaceStatus.temperature =
            gasRecord.GasVol;
      });
    });
  }) as (ZetronSensorDatas: any) => ExecuteOnContext<EntryStatus>, // }) //     // } //     //     console.log("SendWorkScheduleData id:", workScheduleId, " and data: ", data) //     // SendWorkScheduleData: (workScheduleId: any, data: any) => { //     }, //         // entryContext!.update(Actions.updateInOutCount(data)); //         console.log("setWorkScheduleData 123", data) //     setWorkScheduleData: (data?: any) => { //     workScheduleId: schedule?.id, // WorkScheduleHub({
  updateInCount: ((inCount) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.in = inCount;
  }) as (inCount: number) => ExecuteOnContext<EntryStatus>,
  updateOutCount: ((outCount) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.out = outCount;
  }) as (outCount: number) => ExecuteOnContext<EntryStatus>,
  updateInAICount: ((inCountAI) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.inAI = inCountAI;
  }) as (inCountAI: number) => ExecuteOnContext<EntryStatus>,
  updateOutAICount: ((outCountAI) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.outAI = outCountAI;
  }) as (outCountAI: number) => ExecuteOnContext<EntryStatus>,
  updateStandByCount: ((standByCount) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.standBy = standByCount;
  }) as (standByCount: number) => ExecuteOnContext<EntryStatus>,
  updateTotalount: ((totalount) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.total = totalount;
  }) as (totalount: number) => ExecuteOnContext<EntryStatus>,
  updateGasGpsDisplay: ((showGasGPS, workerId) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
      (worker: WorkerStatus) => {
        if (
          worker.id.includes(workerId) &&
          worker.realTimeResourceTypeId?.includes(2)
        ) {
          // test
          worker.showGasGPS = showGasGPS;
        }
        // console.log("updateGasGpsDisplay", stateWrapper.state!.realTimeStatus.workerStatuses, showGasGPS, workerId, worker)
      }
    );
  }) as (
    showGasGPS: boolean,
    workerId: string
  ) => ExecuteOnContext<EntryStatus>,
  updateWatchGpsDisplay: ((showWatchGPS, workerId) => (stateWrapper) => {
    stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
      (worker: WorkerStatus) => {
        if (
          worker.id.includes(workerId) &&
          worker.realTimeResourceTypeId?.includes(2)
        ) {
          // test

          worker.showWatchGPS = showWatchGPS;
        }
        // console.log("updateWatchGpsDisplay", stateWrapper.state!.realTimeStatus.workerStatuses, showWatchGPS, workerId, worker)
      }
    );
  }) as (
    showWatchGPS: boolean,
    workerId: string
  ) => ExecuteOnContext<EntryStatus>,
  updateIsAllowedCheckIn: ((isAllowedCheckIn) => (stateWrapper) => {
    stateWrapper.state!.isAllowedCheckIn = isAllowedCheckIn;
  }) as (isAllowedCheckIn: boolean) => ExecuteOnContext<EntryStatus>,
  setSiteEquipments: ((siteEquipments) => (stateWrapper) => {
    stateWrapper.state!.siteEquipments = siteEquipments;
  }) as (siteEquipments: any[]) => ExecuteOnContext<EntryStatus>,
  updateBodyTemperatureSensor: ((bodyTemperatureSensorDatas) => (stateWrapper) => {

    const temperatureData = Array.isArray(bodyTemperatureSensorDatas)
      ? bodyTemperatureSensorDatas[0]
      : bodyTemperatureSensorDatas;

    const formattedData =
      typeof temperatureData === "number"
        ? { value: temperatureData, sn: "unknown" }
        : temperatureData;


    stateWrapper.state!.realTimeStatus.workerStatuses.forEach(
      (worker: WorkerStatus) => {
        if (worker.equipments && worker.equipments.length > 0) {

          const bodyTempEquipment = worker.equipments.find(
            (e: any) => e.realTimeResourceTypeId === 6
          );
          if (bodyTempEquipment && bodyTempEquipment.serialNumber === formattedData.sn) {
            worker.bodyTemperature = formattedData.value.toString();

          }
        }
      }
    );
  }) as (bodyTemperatureSensorDatas: any) => ExecuteOnContext<EntryStatus>,
};

export const entryContextUpdator = (
  stateWrapper: StateWrapper<EntryStatus>,
  action: ExecuteOnContext<EntryStatus>
) => {
  action(stateWrapper);

  stateWrapper.subStateSelector.forEach((x) => {
    var curState = x.selector(stateWrapper.state);
    var curStateString = JSON.stringify(curState);
    if (curStateString != x.lastState) {
      x.setSubState.forEach((x2) => x2(structuredClone(curState)));
      x.lastState = curStateString;
    }
  });
};

export const useSubState = <T, T2>(
  reactContext: React.Context<
    | {
        state: StateWrapper<T>;
        update: (action: ExecuteOnContext<T>) => void;
      }
    | undefined
  >,
  subStateSelector: (t: T) => T2
): [
  {
    state: StateWrapper<T>;
    update: (action: ExecuteOnContext<T>) => void;
  },
  T2
] => {
  const context = useContext(reactContext);
  const [state, setState] = useState<T2>(
    subStateSelector(context!.state.state)
  );
  // console.log(state)
  useEffect(() => {
    context?.update!(Actions.initiate(subStateSelector, setState));
  }, []);
  return [context!, state];
};
//#endregion
